/**
 * @file picking-format.ts
 * @description Единые формулировки прогресса сборки (phase19.24 §3.2).
 *
 * До контракта плитка шапки считала позиции, строка — единицы, очередь —
 * «0 / 1» без подписи: на одном экране жили три метрики, и кладовщик не
 * понимал, что именно «собрано». Решение пользователя 2026-09-20: единица
 * работы — ПОЗИЦИЯ, а количество в строке всегда с единицей измерения.
 *
 * Тексты собраны в одном модуле, чтобы плитка, sticky-панель, панель отметки,
 * карточка и таблица очереди не расходились по формулировке.
 */

import { pluralRu } from "./plural";

/**
 * Единица по умолчанию. С довеска шага 4 строка задания отдаёт `unit`, и
 * фолбэк остаётся только на пустую строку (фасовка без единицы в каталоге,
 * старый сервер) — «2 / 3» вообще без единицы читать нельзя.
 */
export const PICK_UNIT_FALLBACK = "шт";

/** Единица строки к показу: пустую подменяем «шт», лишние пробелы режем. */
export function pickUnit(unit: string | null | undefined): string {
  return unit?.trim() || PICK_UNIT_FALLBACK;
}

/** «1 из 3 позиций» — главная метрика прогресса задания. */
export function formatPickedPositions(picked: number, total: number): string {
  return `${picked} из ${total} ${pluralRu(total, ["позиции", "позиций", "позиций"])}`;
}

/** «Осталось: 2 позиции» — подпись под полосой прогресса. */
export function formatRemainingPositions(picked: number, total: number): string {
  const left = Math.max(0, total - picked);
  return `Осталось: ${left} ${pluralRu(left, ["позиция", "позиции", "позиций"])}`;
}

/** «1 позиция с недостачей» — вторая строка метрики, когда есть недостачи. */
export function formatShortagePositions(count: number): string {
  return `${count} ${pluralRu(count, ["позиция", "позиции", "позиций"])} с недостачей`;
}

/**
 * «10.000» → «10», «2.500» → «2.5» — количество с сервера к человеческому виду.
 *
 * Prisma отдаёт Decimal строкой со всеми знаками шкалы, и экран писал «на
 * складе 10.000 шт»: кладовщик читал это как «десять тысяч» (находка QA
 * phase19.24 этап 0). Режем только хвостовые нули — разделитель оставляем
 * точкой, как в остальных полях количества, локаль не подключаем.
 *
 * Нечисловое значение возвращаем как есть: лучше показать сырое, чем съесть.
 */
export function formatQtyValue(value: string | number): string {
  const raw = typeof value === "number" ? String(value) : value.trim();
  if (!/^-?\d+\.\d+$/.test(raw)) return raw;
  return raw.replace(/\.?0+$/, "");
}

/**
 * «2 / 3 г» — количество по строке: всегда с единицей строки (§3.2).
 * `unit` обязателен: единицу берём из `PickingTaskItemDto.unit`, а не угадываем.
 */
export function formatItemQty(
  picked: string | number,
  planned: string | number,
  unit: string,
): string {
  return `${formatQtyValue(picked)} / ${formatQtyValue(planned)} ${pickUnit(unit)}`;
}

/** «3 г» — план строки в таблице позиций. */
export function formatPlannedQty(
  planned: string | number,
  unit: string,
): string {
  return `${formatQtyValue(planned)} ${pickUnit(unit)}`;
}

/** Прогресс в процентах для полосы (0..100); пустое задание считаем закрытым. */
export function pickedPercent(picked: number, total: number): number {
  if (total <= 0) return 100;
  return Math.min(100, Math.round((picked / total) * 100));
}
